import { calculateCalcDatasets, getDatasets } from 'api/dataset/dataset.api'
import { getChosenDateFileName, getFileName } from 'helpers/export.helper'
import { clone } from 'helpers/global.helper/global.helper'
import { getUiconfigUidFromId } from 'helpers/uiConfig.helper/uiConfig.helper'
import i18n from 'i18next'
import moment from 'moment'
import uiConfigStore from 'store/uiConfig/uiConfig'
import { DatasetExports } from 'ui/uiConfig/components/ExcelButton/ExcelButton'
import Datetime from 'utils/datetime/datetime'
import { snapshot } from 'valtio'

import { getReturnIdsFromDatasetsInstruction, mapDatasetValuesToTimestamps } from './dataset.helper'
import { type ExportToExcelOptions } from './dataset.helper.types'

export async function exportTabsToExcel(tabs: any, propTimeFormat?: string, chosenDate?: ISODateTime): Promise<void> {
  const XLSX = await import(`xlsx`)
  const exportData = XLSX.utils.book_new()

  tabs.forEach((tab: any, index: number) => {
    const currentDatasetNames = tab.datasets.map((dataset: any) => {
      return dataset.title ?? index
    })

    let title = tab.title
    if (title.startsWith('__')) {
      title = i18n.t(title.split('__')[1])
    } else {
      title = i18n.t(title)
    }

    const customTimeFormat = tab.time_format !== undefined ? tab.time_format : propTimeFormat
    const currentTabData = prepareDatasetsToExcelExportData(
      tab.datasets,
      currentDatasetNames,
      customTimeFormat,
      tab.show_times_in_utc
    )

    XLSX.utils.book_append_sheet(exportData, XLSX.utils.aoa_to_sheet(currentTabData), title)
  })

  if (tabs.length === 0) {
    throw new Error(`Empty workbook`)
  }

  const filename = chosenDate ? getChosenDateFileName(`xlsx`, chosenDate) : getFileName(`xlsx`)

  XLSX.writeFile(exportData, filename, { sheetStubs: true })
}

type FetchUiConfigDatasetsAndExportToExcel = {
  tabs: DatasetExports[]
  datasetStartTime: ISODateTime
  datasetEndTime: ISODateTime
  datasetCreatedAt?: string
  datasetCreatedAtOperator?: DatasetCreatedAtOperators
  time_format: string
}

export function getSuffixesForSameTitleItem(
  firstDatasetReturnId: string,
  secondDatasetReturnId: string
): { suffixForFirstDataset: string; suffixForSecondDataset: string } {
  let newSuffixForFirstDataset = firstDatasetReturnId.split('.').pop()
  let newSuffixForSecondDataset = secondDatasetReturnId.split('.').pop()

  if (newSuffixForFirstDataset === newSuffixForSecondDataset) {
    return { suffixForFirstDataset: '1', suffixForSecondDataset: '2' }
  }

  if (!newSuffixForFirstDataset) {
    newSuffixForFirstDataset = '1'
  }

  if (!newSuffixForSecondDataset) {
    newSuffixForSecondDataset = '2'
  }

  return {
    suffixForFirstDataset: newSuffixForFirstDataset ?? '1',
    suffixForSecondDataset: newSuffixForSecondDataset ?? '2',
  }
}

export async function fetchUiConfigDatasetsAndExportToExcel({
  tabs,
  datasetStartTime,
  datasetEndTime,
  datasetCreatedAt,
  datasetCreatedAtOperator,
  time_format,
}: FetchUiConfigDatasetsAndExportToExcel) {
  const uiConfigSnap = snapshot(uiConfigStore)
  const tabsList = []

  for (let i = 0; i < tabs.length; i++) {
    const uid = getUiconfigUidFromId(uiConfigSnap.uiConfigs, tabs[i].ui_config_id)

    if (!uid) {
      continue
    }

    const uiConfig = uiConfigSnap.getParsedUiConfig(uid, tabs[i].overrideAlias, {
      overrideProps: tabs[i].overrideProps,
    })

    if (!uiConfig) {
      continue
    }

    const items = uiConfig?.props.items
    const datasetInstructions = uiConfig ? (uiConfig?.dataset_instructions ?? []) : []
    const arrayWithoutExcludedInstructions = clone(datasetInstructions)
    const onlyReturnIds = getReturnIdsFromDatasetsInstruction(datasetInstructions)

    const datasets = await getDatasets(
      uiConfig.id,
      uiConfig.version,
      arrayWithoutExcludedInstructions,
      datasetStartTime,
      datasetEndTime,
      {
        datasetCreatedAt,
        datasetCreatedAtOperator,
        overrideAlias: tabs[i].overrideAlias,
        onlyReturnIds,
      }
    )
    const calculatedDatasets = calculateCalcDatasets(arrayWithoutExcludedInstructions, datasets)

    if (datasets.length === 0 || !datasets) {
      throw new Error(i18n.t(`No data found`))
    }

    const datasetsUsedInItems: any = []
    items?.forEach((item) => {
      //loop on items to make sure items decide the order of the excel export
      const datasetThatExistsInItem = calculatedDatasets.find((dataset) => dataset.return_id === item.data_id)
      if (datasetThatExistsInItem) {
        datasetsUsedInItems.push(datasetThatExistsInItem)
      }
    })

    datasetsUsedInItems.forEach((dataset: any, index: number) => {
      const uiConfigItem = uiConfig?.props?.items?.find((item) => item.data_id === dataset.return_id)
      let title: string = uiConfigItem?.title || dataset.return_id

      //if two datasets have the same title you won't be able to distinguish them in export
      const sameNameDataset = datasetsUsedInItems.find((dataset: any) => dataset.title === title)
      if (sameNameDataset) {
        const { suffixForFirstDataset: newSuffixForThisDataset, suffixForSecondDataset: newSuffixForOtherDataset } =
          getSuffixesForSameTitleItem(dataset.return_id, sameNameDataset.return_id)

        sameNameDataset.title = `${title}_${newSuffixForOtherDataset}`
        title = `${title}_${newSuffixForThisDataset}`
      }
      datasetsUsedInItems[index].title = title
    })

    const excelTabObject = {
      title: tabs[i].title,
      ui_config_id: tabs[i].ui_config_id,
      time_format: tabs[i].time_format,
      show_times_in_utc: tabs[i].show_times_in_utc,
      dataset_instructions: datasetInstructions,
      datasets: datasetsUsedInItems,
    }

    if (datasetInstructions.length !== 0) {
      tabsList.push(excelTabObject)
    }
  }

  exportTabsToExcel(tabsList, time_format, datasetStartTime)
}

export function prepareDatasetsForExcelExport(datasets: Dataset[], options?: ExportToExcelOptions): Dataset[] {
  const fillMethod = options?.fillMethod ?? 'null'

  if (fillMethod === 'null') {
    return datasets
  }

  return datasets.map((dataset) => ({
    ...dataset,
    values: mapDatasetValuesToTimestamps(dataset, dataset.times as ISODateTime[], fillMethod),
  }))
}

export async function exportToExcel(
  datasets: Dataset[],
  datasetNames: Record<string, string>,
  options?: ExportToExcelOptions
): Promise<void> {
  const XLSX = await import(`xlsx`)
  const exportData = XLSX.utils.book_new()
  const datasetsForExport = prepareDatasetsForExcelExport(datasets, options)

  const worksheetData = prepareDatasetsToExcelExportData(datasetsForExport, datasetNames, `YYYY-MM-DD HH:mm`)

  XLSX.utils.book_append_sheet(exportData, XLSX.utils.aoa_to_sheet(worksheetData))
  XLSX.writeFile(exportData, getFileName(`xlsx`), { sheetStubs: true })
}

function prepareDatasetsToExcelExportData(
  datasets: any[],
  datasetNames: any,
  timeFormat?: string,
  showInterval?: boolean,
  show_times_in_utc?: boolean
): (string | number | undefined)[][] {
  const worksheet: (string | number | undefined)[][] = [[i18n.t(`Time`)]]
  const datasetNamesInOrder: string[] = []
  const worksheetObj: any = {}

  datasets.forEach((dataset) => {
    const name = dataset.title ?? datasetNames[dataset.return_id]

    if (!name) {
      return
    }

    if (dataset.times.length === 0) {
      return
    }

    datasetNamesInOrder.push(name) // name can be completely without data but must be in correct order

    dataset.times.forEach((time: any, index: number) => {
      const nextTimestamp =
        index === dataset.times.length - 1 ? Datetime.getDayAfterDate(time).startTime : dataset.times[index + 1]

      const timeWithIntervalAndTimeFormat = showInterval
        ? `${moment(time).format(timeFormat)} - ${moment(nextTimestamp).format(timeFormat)}`
        : moment(time).format(timeFormat)
      let currentKey = timeFormat !== undefined ? timeWithIntervalAndTimeFormat : time

      // TODO: How can time ever be a localized string if it's an ISODateTime type?
      if (show_times_in_utc || time === i18n.t('Sum') || time === i18n.t('Average')) {
        currentKey = time
      }

      const currentValue = !isNaN(parseFloat(dataset.values[index])) ? dataset.values[index] : ``

      if (!worksheetObj[currentKey]) {
        worksheetObj[currentKey] = { [name]: isFinite(currentValue) ? currentValue : `` }
      } else {
        worksheetObj[currentKey][name] = isFinite(currentValue) ? currentValue : ``
      }
    })
  })

  //add all headers to first row of export before moving on
  worksheet[0].push(...datasetNamesInOrder)

  Object.entries(worksheetObj).forEach(([key, values]: [string, any]) => {
    const currentRow = [key]

    datasetNamesInOrder.forEach((datasetName) => {
      if (datasetName in values) {
        currentRow.push(values[datasetName])
      } else {
        currentRow.push(``)
      }
    })
    worksheet.push(currentRow)
  })

  return worksheet
}
